import React, { useState, useEffect, useRef, useMemo } from "react";
import YhInput from "../../../../../ui/YhInput";
import YhSelect from "../../../../../ui/YhSelect";
import YhMultiSelect from "../../../../../ui/YhMultiSelect";
import YhButton from "../../../../../ui/YhButton";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import notifiers from "../../../../../global/constants/NotificationConstants";
import customErrorHandler from "../../../../../utils/customErrorHandler";
import {
  getFinalBodyStructure,
  handleValidation,
  initialState,
  validateInput,
} from "./Validation";
import {
  getProjectsByDeveloper,
  getProjectUnitOptions,
  searchProjects,
  addForm,
  getDeveloper,
  updateProjectDetail,
  listWebsiteForms,
} from "../StepperService";
import { DndProvider } from "react-dnd";
import { getSharedDndManager } from "./DragThingsToBoxes/dndSingleton";
import { BoxGrid, buildFormSections, defaultWebsiteFormFields, resolvePreviewProjectOptions } from "./DragThingsToBoxes/Box";
import { Dustbin } from "./DragThingsToBoxes/Dustbin";
import BuilderPage from "./DragThingsToBoxes/EditForm";
import FormSettingsPanel, {
  defaultFormSettings,
  defaultFormSeo,
  defaultGoogleSheet,
  defaultMediaPanel,
} from "./DragThingsToBoxes/FormSettingsPanel";
import OrganizationSelect from "../../../../../Shared/Organization/OrganizationSelect";
import { fetchAutomationRoutingOptions } from "../../../../../services/organizationService";
import { buildPublicFormUrl } from "../../../feedbackPublicUrl";
import InputIcon from "../../../../../assets/icons/text.svg";
import TextareaIcon from "../../../../../assets/icons/textarea.svg";
import CheckboxIcon from "../../../../../assets/icons/checkbox.svg";
import RadioIcon from "../../../../../assets/icons/radio.svg";

const mapUnitOptions = (projectId: string, priceList: any[] = []) =>
  (priceList || []).map((item: any, index: number) => {
    const bedroom = String(item.bedroom || item.unitType || "").trim();
    const area = String(item.carpetArea || item.area || "").trim();
    const price = String(item.price || item.totalPrice || "").trim();
    const parts = [
      bedroom,
      area ? `${area} sqft` : "",
      price ? `₹${price}` : "",
    ].filter(Boolean);
    return {
      id: `${projectId}_${index}`,
      label: parts.length ? parts.join(" · ") : `Unit ${index + 1}`,
      bedroom: item.bedroom,
      sqft: item.carpetArea,
    };
  });

const sortByLabel = (rows: any[] = []): any[] =>
  [...rows].sort((a, b) =>
    String(a?.label || "").localeCompare(String(b?.label || ""), undefined, {
      sensitivity: "base",
    })
  );

interface CustomProps {
  nextStep?: () => void | undefined;
  userId: String;
  idd: string;
  setIdd: Function;
  fromSettingOrLogout?: any;
  handleCloseAddTeam?: any;
  edit: Boolean;
  editRowData: any;
  registerStepActions?: (actions: { submit: () => void | Promise<void> }) => void;
  hideFooterButtons?: boolean;
  onPreviewReady?: (open: () => void) => void;
}
const PersonalDetails = (props: CustomProps) => {
  const [personalDetailState, setPersonalDetailState] = useState<any>(
    initialState({})
  );
  const [option, setOption] = useState<any>({
    developer: [],
    project: [],
    unitConfigList: [],
  });
  const [loadingDevelopers, setLoadingDevelopers] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const projectSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [items, setItems] = useState<any>([]);
  const [inputId, setInputId] = useState<string>("");
  const [settingsTab, setSettingsTab] = useState<"form" | "field">("form");
  const [formSettings, setFormSettings] = useState(defaultFormSettings());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState("");
  const [hasWebsiteForm, setHasWebsiteForm] = useState(false);
  const baseHtmlType = [
    { id: "field12", type: "section", label: "Section", icon: InputIcon },
    { id: "field10", type: "heading", label: "Heading", icon: InputIcon },
    { id: "field1", type: "text", label: "Input", icon: InputIcon },
    { id: "field13", type: "number", label: "Number", icon: InputIcon },
    { id: "field3", type: "textarea", label: "Text Area", icon: TextareaIcon },
    { id: "field4", type: "checkbox", label: "Checkbox", icon: CheckboxIcon },
    { id: "field9", type: "file", label: "Image Upload", icon: InputIcon },
    { id: "field2", type: "select", label: "Select", icon: InputIcon },
    { id: "field5", type: "radio", label: "Radio", icon: RadioIcon },
    { id: "field8", type: "date", label: "Date", icon: InputIcon },
    { id: "field14", type: "time", label: "Time", icon: InputIcon },
    { id: "field6", type: "email", label: "Email", icon: InputIcon },
    { id: "field7", type: "phone", label: "Phone", icon: InputIcon },
    { id: "field15", type: "address", label: "Address", icon: InputIcon },
    { id: "field17", type: "button", label: "Button", icon: InputIcon },
    { id: "field18", type: "fillblanks", label: "Fill Blanks", icon: InputIcon },
    { id: "field19", type: "daterange", label: "Date Range", icon: InputIcon },
    { id: "field20", type: "rating", label: "Rating", icon: InputIcon },
    { id: "field11", type: "divider", label: "Divider", icon: InputIcon },
  ];
  const htmlType = useMemo(() => {
    if (
      formSettings.formType !== "feedback" &&
      formSettings.formType !== "registration"
    ) {
      return baseHtmlType;
    }
    const projectPalette = {
      id: "field21",
      type: "project",
      label: "Project Interest",
      icon: RadioIcon,
    };
    const next = [...baseHtmlType];
    const radioIdx = next.findIndex((f) => f.type === "radio");
    next.splice(radioIdx + 1, 0, projectPalette);
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSettings.formType]);
  const countryData = [
    {
      label: "India",
      code: "IN",
      dialCode: "+91",
      flag: "https://example.com/india_flag.jpg",
      capital: "New Delhi",
      class: <span className="fi fi-in"></span>,
    },
    {
      label: "USA",
      code: "US",
      dialCode: "+1",
      flag: "https://example.com/usa_flag.jpg",
      capital: "Washington, D.C.",
      class: <span className="fi fi-us"></span>,
    },
    {
      label: "UAE",
      code: "AE",
      dialCode: "+971",
      flag: "https://example.com/uae_flag.jpg",
      capital: "Abu Dhabi",
      class: <span className="fi fi-ae"></span>,
    },
  ];
  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formSettings.formType !== "website") return;
    if (items.length > 0) return;
    setItems(defaultWebsiteFormFields());
  }, [formSettings.formType, items.length]);

  useEffect(() => {
    if (!organizationId) {
      setHasWebsiteForm(false);
      return;
    }
    let cancelled = false;
    listWebsiteForms(organizationId)
      .then((rows) => {
        if (cancelled) return;
        const editId = String(
          props.editRowData?._id || personalDetailState?.id?.value || ""
        );
        setHasWebsiteForm(
          (rows || []).some((row: any) => String(row?._id || "") !== editId)
        );
      })
      .catch(() => {
        if (!cancelled) setHasWebsiteForm(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, props.editRowData?._id]);

  useEffect(() => {
    if (
      (formSettings.formType !== "feedback" &&
        formSettings.formType !== "registration" &&
        formSettings.formType !== "website") ||
      !organizationId
    )
      return;
    (async () => {
      try {
        const res = await fetchAutomationRoutingOptions(organizationId);
        const projects = Array.isArray(res?.data?.projects)
          ? res.data.projects
          : [];
        setOption((prev: any) => ({
          ...prev,
          project: projects.map((p: any) => ({
            id: p.projectId || p._id,
            label: p.projectName || p.project_name || "Project",
            unitOptions: [],
          })),
        }));
      } catch (_) {
        /* keep existing project options */
      }
    })();
  }, [formSettings.formType, organizationId]);

  useEffect(() => {
    if (props.editRowData) {
      hydrateFromEditRow(props.editRowData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.editRowData]);

  const handleOnChangeInputField = (event: any) => {
    const name: any = event.target.name;
    const value: any = event.target.value;
    const isGoForward: any = validateInput(name, value);
    if (!isGoForward) return;
    setPersonalDetailState((prevFormData: any) => ({
      ...prevFormData,
      [name]: { error: "", value },
    }));
  };

  const mapProjectOption = (i: any) => {
    const id = String(i.projectId || i._id || "");
    const name = String(i.project_name || i.projectName || "Untitled project");
    const uid = String(i.project_uid || i.uid || "").trim();
    return {
      id,
      label: uid ? `${name} (${uid})` : name,
      keywords: [name, uid, id].filter(Boolean).join(" "),
      project_uid: uid,
      unitOptions: mapUnitOptions(id, i.priceList || []),
    };
  };

  const loadUnitsForProject = async (projectId: string) => {
    try {
      const res = await getProjectUnitOptions(projectId);
      const row = Array.isArray(res?.data) ? res.data[0] : null;
      return mapUnitOptions(projectId, row?.priceList || []);
    } catch {
      return [];
    }
  };

  const handleAutocompleteChange = async (name: string, value: any) => {
    if (!value) return;

    const isGoForward: any = validateInput(name, value);
    if (!isGoForward) return;

    if (name === "developerId") {
      setPersonalDetailState((prev: any) => ({
        ...prev,
        developerId: {
          error: "",
          value: value.label || value.developer_name || "",
          id: value.id,
        },
        projectId: { error: "", value: "", id: "" },
        unitConfig: { error: "", value: "", id: "" },
      }));
      setLoadingProjects(true);
      try {
        const response = await getProjectsByDeveloper(value.id);
        setOption((prev: any) => ({
          ...prev,
          project: sortByLabel(
            (response?.data || []).map((i: any) => mapProjectOption(i))
          ),
          unitConfigList: [],
        }));
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
        );
        setOption((prev: any) => ({
          ...prev,
          project: [],
          unitConfigList: [],
        }));
      } finally {
        setLoadingProjects(false);
      }
      return;
    }

    if (name === "projectId") {
      setPersonalDetailState((prev: any) => ({
        ...prev,
        projectId: {
          error: "",
          value: value.label || value.project_name || "",
          id: value.id,
        },
        unitConfig: { error: "", value: "", id: "" },
      }));

      let units = value.unitOptions || [];
      if (!units.length) {
        setLoadingProjects(true);
        units = await loadUnitsForProject(value.id);
        setLoadingProjects(false);
      }
      setOption((prev: any) => ({
        ...prev,
        unitConfigList: units,
        project: (prev.project || []).map((p: any) =>
          String(p.id) === String(value.id) ? { ...p, unitOptions: units } : p
        ),
      }));
      return;
    }

    if (name === "unitConfig") {
      setPersonalDetailState((prev: any) => ({
        ...prev,
        unitConfig: {
          error: "",
          value: value.map((v: any) => v.label),
          data: value,
        },
      }));
      return;
    }

    setPersonalDetailState((prev: any) => ({
      ...prev,
      [name]: {
        error: "",
        value: value.label,
        id: value.id,
      },
    }));
  };

  const handleProjectSearch = (q: string) => {
    if (projectSearchTimer.current) clearTimeout(projectSearchTimer.current);
    const query = String(q || "").trim();
    const developerId = personalDetailState?.developerId?.id;
    if (query.length < 2) return;
    projectSearchTimer.current = setTimeout(async () => {
      setLoadingProjects(true);
      try {
        const response = await searchProjects(query, developerId || undefined);
        const rows = sortByLabel(
          (response?.data || []).map((i: any) => mapProjectOption(i))
        );
        setOption((prev: any) => {
          const byId = new Map<string, any>();
          (prev.project || []).forEach((p: any) => byId.set(String(p.id), p));
          rows.forEach((p: any) => byId.set(String(p.id), p));
          return {
            ...prev,
            project: sortByLabel(Array.from(byId.values())),
          };
        });
      } catch (_) {
        /* keep local list */
      } finally {
        setLoadingProjects(false);
      }
    }, 300);
  };
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const inputs = document.getElementsByTagName("input");
      const currentIndex = Array.from(inputs).indexOf(
        event.target as HTMLInputElement
      );
      const nextIndex = currentIndex + 1;

      if (nextIndex < inputs.length) {
        inputs[nextIndex].focus();
      } else {
        // If the last input field is reached, trigger the submitForm function
        submitForm();
      }
    }
  };


  const normalizeDynamicFields = (raw: any) => {
    let fields = raw ?? [];
    if (typeof fields === "string") {
      try {
        fields = JSON.parse(fields);
      } catch {
        fields = [];
      }
    }
    if (!Array.isArray(fields)) return [];
    return fields.map((item: any) => {
      let next = item;
      if (item?.type === "section") {
        next = {
          ...item,
          name: item.name || item.label || "New Section",
          sectionType: item.sectionType === "step" ? "step" : "normal",
          access:
            item.sectionAccess === "restricted" || item.access === "restricted"
              ? "restricted"
              : "all",
          sectionAccess:
            item.sectionAccess === "restricted" || item.access === "restricted"
              ? "restricted"
              : "all",
          sectionAllowedUsers: Array.isArray(item.sectionAllowedUsers)
            ? item.sectionAllowedUsers
            : [],
          conditions: Array.isArray(item.conditions) ? item.conditions : [],
          conditionOperator:
            item.conditionOperator === "OR" ? "OR" : "AND",
        };
      } else if (item && typeof item === "object") {
        next = {
          ...item,
          conditions: Array.isArray(item.conditions) ? item.conditions : [],
          conditionOperator:
            item.conditionOperator === "OR" ? "OR" : "AND",
        };
      }
      if (
        (next.type === "select" ||
          next.type === "checkbox" ||
          next.type === "radio") &&
        typeof next.options === "string"
      ) {
        return {
          ...next,
          options: next.options
            .split(";")
            .map((opt: string) => opt.trim())
            .filter((opt: string) => opt),
        };
      }
      return next;
    });
  };

  const hydrateFromEditRow = async (row: any) => {
    if (!row) return;

    setItems(normalizeDynamicFields(row.dynamicFields));

    setFormSettings({
      description: row.formDescription || "",
      theme: row.formTheme || "#841C39",
      enableForm: row.enableForm !== false,
      showProgressBar: row.showProgressBar !== false,
      allowMultiple: !!row.allowMultipleSubmissions,
      enableCaptcha: !!row.enableCaptcha,
      enablePaymentMethod: !!row.enablePaymentMethod,
      enableAadhaarVerification: !!row.enableAadhaarVerification,
      formType:
        row.formType === "feedback" ||
        row.formType === "registration" ||
        row.formType === "website"
          ? row.formType
          : "normal",
      tokenMaxDays:
        row.tokenMaxDays != null && row.tokenMaxDays !== ""
          ? Number(row.tokenMaxDays)
          : 90,
      defaultLeadSource: row.defaultLeadSource || "",
      sendWhatsAppOnSubmit: !!row.sendWhatsAppOnSubmit,
      onSubmitWhatsappTemplateWid: row.onSubmitWhatsappTemplateWid || "",
      onSubmitWhatsappTemplateName: row.onSubmitWhatsappTemplateName || "",
      mediaPanel: { ...defaultMediaPanel(), ...(row.mediaPanel || {}) },
      seo: { ...defaultFormSeo(), ...(row.seo || {}) },
      googleSheet: { ...defaultGoogleSheet(), ...(row.googleSheet || {}) },
      formLayout:
        row.formType === "website" || row.formLayout === "single"
          ? "single"
          : "steps",
      publicUrl:
        buildPublicFormUrl({
          ...row,
          defaultLeadSource: row.defaultLeadSource || "",
        }) ||
        row.publicUrl ||
        "",
      publicSlug: row.publicSlug || "",
      orgSlug: row.orgSlug || "",
      projectSlug: row.projectSlug || "",
      formScope: row.formScope || "",
    });

    if (row.organizationId) {
      setOrganizationId(String(row.organizationId));
    }

    setPersonalDetailState((prevFormData: any) => ({
      ...prevFormData,
      form_name: { error: "", value: row.form_name ?? "" },
      developerId: {
        error: "",
        value: row.developer_name ?? "",
        id: row.developerId && row.developerId !== "NA" ? row.developerId : "",
      },
      projecttypeId: {
        error: "",
        value: row.projecttype_name ?? "",
        id: row.projecttypeId ?? "",
      },
      projectId: {
        error: "",
        value: row.project_name ?? "",
        id: row.projectId && row.projectId !== "NA" ? row.projectId : "",
      },
      unitConfig: {
        error: "",
        value: Array.isArray(row.unitConfig) ? row.unitConfig : [],
        data: Array.isArray(row.unitConfig) ? row.unitConfig : [],
      },
      leadPhone: { error: "", value: row.leadPhone ?? "" },
      leadName: { error: "", value: row.leadName ?? "" },
      leadEmail: { error: "", value: row.leadEmail ?? "" },
      phoneCountryCode: {
        error: "",
        value: row.phoneCountryCode ?? "",
      },
      id: { error: "", value: row._id ?? row.id ?? "" },
    }));

    if (!row.developerId || row.developerId === "NA") return;
    try {
      setLoadingProjects(true);
      const response = await getProjectsByDeveloper(row.developerId);
      const projects = sortByLabel(
        (response?.data || []).map((i: any) => mapProjectOption(i))
      );
      const selectedProject = projects.find(
        (p) => String(p.id) === String(row.projectId)
      );
      let units = selectedProject?.unitOptions || [];
      if (row.projectId && (!units || !units.length)) {
        units = await loadUnitsForProject(String(row.projectId));
      }
      setOption((prev: any) => ({
        ...prev,
        project: projects,
        unitConfigList: units,
      }));
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoadingProjects(false);
    }
  };

  const init = async () => {
    try {
      setLoadingDevelopers(true);
      const developerData = await getDeveloper();

      setOption((prev: any) => ({
        ...prev,
        developer: sortByLabel(
          (developerData?.data || []).map((i: any) => ({
            id: i._id,
            label: i.developer_name || i.name || "Untitled developer",
            keywords: String(i.developer_name || i.name || ""),
          }))
        ),
      }));

      if (props.editRowData) {
        await hydrateFromEditRow(props.editRowData);
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoadingDevelopers(false);
    }
  };

  const submitForm = async () => {
    const isPublicForm =
      formSettings.formType === "feedback" ||
      formSettings.formType === "registration";
    // Developer / Project are optional for all form types
    const validatedObject: any = handleValidation(
      { ...personalDetailState },
      { skipProject: true, skipDeveloper: true }
    );
    if (!organizationId && isPublicForm) {
      openErrorNotification(
        "Please select an organization for this public form"
      );
      return;
    }
    if (validatedObject.form_name?.error) {
      setPersonalDetailState({ ...validatedObject });
      return;
    }
    try {
      const normalizedItems = items.map((item: any) => {
        if (
          (item.type === "select" ||
            item.type === "checkbox" ||
            item.type === "radio") &&
          typeof item.options === "string"
        ) {
          return {
            ...item,
            options: item.options
              .split(";")
              .map((opt: string) => opt.trim())
              .filter((opt: string) => opt),
          };
        }
        return item;
      });

      const body = {
        ...personalDetailState,
        dynamicFields: normalizedItems,
      };

      const payload = getFinalBodyStructure(body);
      payload.formDescription = formSettings.description || "";
      payload.formTheme = formSettings.theme || "#841C39";
      payload.enableForm =
        formSettings.formType === "website" ? true : formSettings.enableForm;
      payload.showProgressBar = formSettings.showProgressBar;
      payload.allowMultipleSubmissions = formSettings.allowMultiple;
      payload.enableCaptcha = formSettings.enableCaptcha;
      payload.enablePaymentMethod = formSettings.enablePaymentMethod;
      payload.enableAadhaarVerification = formSettings.enableAadhaarVerification;
      payload.formType = formSettings.formType || "normal";
      payload.tokenMaxDays =
        formSettings.tokenMaxDays != null ? formSettings.tokenMaxDays : 90;
      payload.defaultLeadSource = formSettings.defaultLeadSource || "";
      payload.sendWhatsAppOnSubmit = !!formSettings.sendWhatsAppOnSubmit;
      payload.onSubmitWhatsappTemplateWid =
        formSettings.onSubmitWhatsappTemplateWid || "";
      payload.onSubmitWhatsappTemplateName =
        formSettings.onSubmitWhatsappTemplateName || "";
      payload.mediaPanel = formSettings.mediaPanel || defaultMediaPanel();
      payload.seo = formSettings.seo || defaultFormSeo();
      payload.googleSheet = formSettings.googleSheet || defaultGoogleSheet();
      payload.formLayout =
        formSettings.formLayout === "single" ? "single" : "steps";
      payload.organizationId = organizationId || undefined;
      if (!payload.developerId) payload.developerId = "NA";
      if (formSettings.formType === "website") {
        payload.projectId = "NA";
        payload.formLayout = "single";
        if (hasWebsiteForm && !props.edit) {
          openErrorNotification(
            "This organization already has a website form. Edit that form instead."
          );
          return;
        }
      } else if (!payload.projectId) {
        payload.projectId = "NA";
      }

      if (props.edit) {
        payload.id =
          payload.id ||
          props.idd ||
          personalDetailState?.id?.value ||
          "";
        if (!payload.status) payload.status = "1";
        const response = await updateProjectDetail(payload);
        if (response?.success) {
          if (response?.data) {
            setFormSettings((prev) => ({
              ...prev,
              publicUrl:
                buildPublicFormUrl(response.data) ||
                response.data.publicUrl ||
                prev.publicUrl,
              publicSlug: response.data.publicSlug || prev.publicSlug,
              orgSlug: response.data.orgSlug || prev.orgSlug,
              projectSlug: response.data.projectSlug || prev.projectSlug,
              formScope: response.data.formScope || prev.formScope,
            }));
          }
          openSuccessNotification(response?.message);
          props.handleCloseAddTeam();
        } else {
          customErrorHandler(response);
        }
      } else {
        const response = await addForm(payload);
        if (response?.success) {
          if (response?.data) {
            setFormSettings((prev) => ({
              ...prev,
              publicUrl:
                buildPublicFormUrl(response.data) ||
                response.data.publicUrl ||
                prev.publicUrl,
              publicSlug: response.data.publicSlug || prev.publicSlug,
              orgSlug: response.data.orgSlug || prev.orgSlug,
              projectSlug: response.data.projectSlug || prev.projectSlug,
              formScope: response.data.formScope || prev.formScope,
            }));
          }
          openSuccessNotification(response?.message);
          props.handleCloseAddTeam();
        } else {
          customErrorHandler(response);
        }
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const submitFormRef = useRef(submitForm);
  submitFormRef.current = submitForm;

  useEffect(() => {
    props.registerStepActions?.({
      submit: () => submitFormRef.current(),
    });
  }, [props.registerStepActions]);

  useEffect(() => {
    props.onPreviewReady?.(() => setPreviewOpen(true));
  }, [props.onPreviewReady]);

  useEffect(() => {
    if (inputId) setSettingsTab("field");
  }, [inputId]);

  const unitConfigValues = Array.isArray(personalDetailState?.unitConfig?.value)
    ? personalDetailState.unitConfig.value
    : [];
  const unitConfigOptions: { label: string; value: string | number }[] =
    sortByLabel(
      (option.unitConfigList || [])
        .map((opt: any) => ({
          label: String(opt.label || "").trim() || String(opt.id || "Unit"),
          value: opt.id as string | number,
        }))
        .filter(
          (o: { label: string; value: string | number }) =>
            o.value != null && o.value !== ""
        )
    );
  const selectedUnitIds = unitConfigValues.map((item: any) =>
    typeof item === "object" && item != null ? item.id : item
  );

  const handleUnitConfigChange = (values: Array<string | number>) => {
    const selected = (option.unitConfigList || []).filter((opt: any) =>
      values.map(String).includes(String(opt.id))
    );
    setPersonalDetailState((prev: any) => ({
      ...prev,
      unitConfig: {
        error: "",
        value: selected,
        data: selected,
      },
    }));
  };

  const phoneCountryCodeValue =
    countryData.find(
      (c) => c.dialCode === personalDetailState?.phoneCountryCode?.dialCode
    )?.code ||
    personalDetailState?.phoneCountryCode?.code ||
    "";

  const dndManager = useMemo(() => getSharedDndManager(), []);

  return (
    <div className="h-full min-h-0">
      {dndManager ? (
      <DndProvider manager={dndManager}>
        <div className="flex h-full min-h-0 gap-4">
        {/* Left: Components */}
        <aside className="hidden h-full min-h-0 w-[220px] shrink-0 flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)] lg:flex">
          <div className="shrink-0 border-b border-[#EEF0F5] px-4 py-3">
            <h3 className="text-sm font-bold text-[#111827]">Components</h3>
            <p className="mt-0.5 text-xs text-[#6B7280]">Drag or click to add</p>
          </div>
          <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain p-2.5 [scrollbar-gutter:stable]">
            {htmlType.map((e: any) => (
              <BoxGrid
                key={e.id}
                name={e.type}
                icon={e.icon}
                setItems={setItems}
                items={items}
              />
            ))}
          </div>
        </aside>

        {/* Center: Canvas */}
        <div className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto pb-2">
          <section className="rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
            <h3 className="mb-4 text-base font-bold text-[#111827]">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <OrganizationSelect
                  label="Organization"
                  required
                  value={organizationId}
                  onChange={setOrganizationId}
                />
              </div>
              <YhInput
                label="Form Name"
                placeholder="Form Name"
                error={personalDetailState?.form_name?.error}
                value={personalDetailState?.form_name?.value || ""}
                onChange={handleOnChangeInputField}
                name="form_name"
                type="text"
                onKeyPress={handleKeyPress}
              />
              {formSettings.formType === "website" ? (
                <div className="rounded-xl border border-[#F9F0F4] bg-[#FDFBFC] px-3 py-2.5 text-sm text-[#6B7280]">
                  Applies to all Yhataw project pages. Use Landing popup → offer
                  to promote on selected projects only.
                </div>
              ) : formSettings.formType === "feedback" ||
              formSettings.formType === "registration" ? (
                <>
                  <YhSelect
                    label="Project (optional)"
                    placeholder="Org-level if empty"
                    value={personalDetailState?.projectId?.id || ""}
                    error={personalDetailState?.projectId?.error}
                    options={[
                      {
                        label: "Organization level (no project)",
                        value: "",
                      },
                      ...(option.project || []).map((opt: any) => ({
                        label: opt.label,
                        value: opt.id,
                      })),
                    ]}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        setPersonalDetailState((prev: any) => ({
                          ...prev,
                          projectId: { error: "", value: "", id: "" },
                        }));
                        return;
                      }
                      const obj = option.project?.find(
                        (opt: any) => opt.id === val
                      );
                      if (obj) handleAutocompleteChange("projectId", obj);
                    }}
                  />
                </>
              ) : (
                <>
                  <YhSelect
                    label="Developer Name"
                    placeholder="Select Developer (optional)"
                    searchPlaceholder="Search developer..."
                    searchable
                    clearable
                    loading={loadingDevelopers}
                    value={personalDetailState?.developerId?.id || ""}
                    error={personalDetailState?.developerId?.error}
                    options={(option.developer || []).map((opt: any) => ({
                      label: opt.label,
                      value: opt.id,
                      keywords: opt.keywords || opt.label,
                    }))}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        setPersonalDetailState((prev: any) => ({
                          ...prev,
                          developerId: { error: "", value: "", id: "" },
                          projectId: { error: "", value: "", id: "" },
                        }));
                        setOption((prev: any) => ({
                          ...prev,
                          project: [],
                          unitConfigList: [],
                        }));
                        return;
                      }
                      const obj = option.developer?.find(
                        (opt: any) => opt.id === e.target.value
                      );
                      if (obj) handleAutocompleteChange("developerId", obj);
                    }}
                  />
                  <YhSelect
                    label="Project Name"
                    placeholder="Select Your Project Name (optional)"
                    searchPlaceholder="Search project name or ID (PC_…)"
                    searchable
                    clearable
                    loading={loadingProjects}
                    value={personalDetailState?.projectId?.id || ""}
                    error={personalDetailState?.projectId?.error}
                    options={(option.project || []).map((opt: any) => ({
                      label: opt.label,
                      value: opt.id,
                      keywords: opt.keywords || opt.label,
                    }))}
                    onSearchChange={handleProjectSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        setPersonalDetailState((prev: any) => ({
                          ...prev,
                          projectId: { error: "", value: "", id: "" },
                          unitConfig: { error: "", value: "", id: "" },
                        }));
                        setOption((prev: any) => ({
                          ...prev,
                          unitConfigList: [],
                        }));
                        return;
                      }
                      const obj = option.project?.find(
                        (opt: any) => opt.id === e.target.value
                      );
                      if (obj) handleAutocompleteChange("projectId", obj);
                    }}
                  />
                  <div className="md:col-span-2">
                    <YhMultiSelect
                      label="Unit Configuration"
                      placeholder="Select unit configuration"
                      searchPlaceholder="Search unit configuration..."
                      options={unitConfigOptions}
                      value={selectedUnitIds}
                      showSelectAll
                      searchable
                      showOptionIcon={false}
                      maxVisibleChips={2}
                      clearable
                      error={personalDetailState?.unitConfig?.error}
                      onChange={handleUnitConfigChange}
                    />
                  </div>
                </>
              )}
            </div>
          </section>

          {formSettings.formType !== "feedback" &&
          formSettings.formType !== "registration" &&
          formSettings.formType !== "website" ? (
          <section className="rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
            <h3 className="mb-4 text-base font-bold text-[#111827]">
              Lead Details
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <YhInput
                label="Name"
                disabled
                value={personalDetailState?.leadName?.value || ""}
                onChange={handleOnChangeInputField}
                name="leadName"
                type="text"
              />
              <YhInput
                label="Work Email"
                disabled
                value={personalDetailState?.leadEmail?.value || ""}
                onChange={handleOnChangeInputField}
                name="leadEmail"
                type="text"
              />
              <YhSelect
                label="Code"
                placeholder="Code"
                disabled
                value={phoneCountryCodeValue}
                options={countryData.map((c) => ({
                  label: `${c.label} (${c.dialCode})`,
                  value: c.code,
                }))}
                onChange={() => {}}
              />
              <YhInput
                label="Phone Number"
                placeholder="XXX XXX XXXX"
                disabled
                error={personalDetailState?.leadPhone?.error}
                value={personalDetailState?.leadPhone?.value || ""}
                onChange={handleOnChangeInputField}
                name="leadPhone"
                type="number"
                onKeyPress={handleKeyPress}
              />
            </div>
          </section>
          ) : null}

          <section className="rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
            <h3 className="mb-1 text-base font-bold text-[#111827]">
              Additional Details
            </h3>
            <p className="mb-4 text-sm text-[#6B7280]">
              Drag fields by the ⋮⋮ handle to reorder · click left components to add
            </p>
            <div className="mb-3 flex flex-wrap gap-2 lg:hidden">
              {htmlType.map((e: any) => (
                <BoxGrid
                  key={`m-${e.id}`}
                  name={e.type}
                  icon={e.icon}
                  setItems={setItems}
                  items={items}
                />
              ))}
            </div>
            <Dustbin
              allowedDropEffect="any"
              items={items}
              setItems={setItems}
              setInputId={setInputId}
              inputId={inputId}
            />
          </section>

          {!props.hideFooterButtons && (
            <div className="flex justify-start pt-2">
              <YhButton
                variant="primary"
                onClick={submitForm}
                className="min-w-[200px]"
              >
                Save
              </YhButton>
            </div>
          )}
        </div>

        {/* Right: Settings */}
        <aside className="hidden h-full min-h-0 w-[300px] shrink-0 flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)] xl:flex">
          <div className="flex shrink-0 border-b border-[#EEF0F5] p-2">
            <button
              type="button"
              onClick={() => setSettingsTab("form")}
              className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                settingsTab === "form"
                  ? "bg-[#F9F0F4] text-[#841C39]"
                  : "text-[#6B7280] hover:bg-[#F9FAFB]"
              }`}
            >
              Form Settings
            </button>
            <button
              type="button"
              onClick={() => setSettingsTab("field")}
              className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                settingsTab === "field"
                  ? "bg-[#F9F0F4] text-[#841C39]"
                  : "text-[#6B7280] hover:bg-[#F9FAFB]"
              }`}
            >
              Field Settings
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {settingsTab === "form" ? (
              <FormSettingsPanel
                settings={formSettings}
                onChange={setFormSettings}
                organizationId={organizationId}
                hasWebsiteForm={hasWebsiteForm}
                lockWebsiteType={
                  !!props.edit && formSettings.formType === "website"
                }
                projectOptions={(option.project || []).map((opt: any) => ({
                  label: opt.label,
                  value: String(opt.id),
                }))}
              />
            ) : (
              <BuilderPage
                inputId={inputId}
                items={items}
                setItems={setItems}
                setInputId={setInputId}
                keepOpen
                availableProjects={option.project || []}
              />
            )}
          </div>
        </aside>
      </div>

      {previewOpen ? (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/40 p-4">
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white shadow-xl"
            style={
              {
                ["--form-theme" as string]: formSettings.theme || "#841C39",
              } as React.CSSProperties
            }
          >
            <div
              className="h-1.5 w-full rounded-t-2xl"
              style={{ backgroundColor: formSettings.theme || "#841C39" }}
            />
            <div className="p-6">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <p
                    className="mb-1 text-xs font-semibold uppercase tracking-wide"
                    style={{ color: formSettings.theme || "#841C39" }}
                  >
                    Form theme preview
                  </p>
                  <h2 className="text-xl font-bold text-[#111827]">
                    {personalDetailState?.form_name?.value || "Form Preview"}
                  </h2>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    {formSettings.description ||
                      "Preview of your lead form fields."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="rounded-lg px-3 py-1.5 text-sm font-semibold text-[#6B7280] hover:bg-[#F3F4F6]"
                >
                  Close
                </button>
              </div>

              <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#EEF0F5] bg-[#F8F9FC] px-3 py-2">
                <span
                  className="h-4 w-4 shrink-0 rounded-full"
                  style={{
                    backgroundColor: formSettings.theme || "#841C39",
                    boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${formSettings.theme || "#841C39"}`,
                  }}
                />
                <span className="text-xs font-medium text-[#6B7280]">
                  Theme color:
                </span>
                <span
                  className="text-xs font-bold uppercase"
                  style={{ color: formSettings.theme || "#841C39" }}
                >
                  {formSettings.theme || "#841C39"}
                </span>
              </div>

              <div className="space-y-4">
                {["Name", "Work Email", "Phone Number"].map((label) => (
                  <div key={label}>
                    <label className="mb-1.5 block text-sm font-semibold text-[#374151]">
                      {label}
                    </label>
                    <input
                      disabled
                      placeholder={label}
                      className="w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none"
                      style={{
                        borderColor: `${formSettings.theme || "#841C39"}55`,
                      }}
                    />
                  </div>
                ))}

                {items.length === 0 ? (
                  <p className="text-sm text-[#9CA3AF]">
                    No additional fields yet.
                  </p>
                ) : (
                  (() => {
                    const theme = formSettings.theme || "#841C39";
                    const hasSections = items.some(
                      (f: any) => f.type === "section"
                    );
                    type PreviewSection = {
                      label: string;
                      section: any | null;
                      fields: any[];
                    };
                    const sections: PreviewSection[] = hasSections
                      ? buildFormSections(items)
                      : [{ label: "", section: null, fields: items }];
                    const stepSections = sections.filter(
                      (s: PreviewSection) =>
                        s.section?.sectionType === "step"
                    );
                    const stepTotal = stepSections.length;

                    const renderField = (item: any) => {
                      if (item.type === "divider") {
                        return (
                          <hr
                            key={item.id}
                            className="my-2 border-t-2"
                            style={{ borderColor: theme }}
                          />
                        );
                      }
                      if (item.type === "button") {
                        return (
                          <button
                            key={item.id}
                            type="button"
                            disabled
                            className="mt-1 w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white"
                            style={{ backgroundColor: theme }}
                          >
                            {item.name || "Button"}
                          </button>
                        );
                      }
                      if (item.type === "heading") {
                        return (
                          <h3
                            key={item.id}
                            className="text-base font-bold capitalize"
                            style={{ color: theme }}
                          >
                            {item.name || item.placeholder || "Heading"}
                          </h3>
                        );
                      }
                      if (item.type === "rating") {
                        const emoji = item.ratingStyle !== "stars";
                        return (
                          <div key={item.id}>
                            <label className="mb-1.5 block text-sm font-semibold capitalize text-[#374151]">
                              {item.name || "Rating"}
                            </label>
                            {emoji ? (
                              <div className="flex flex-wrap gap-2 text-xl">
                                {["😞", "😕", "😐", "🙂", "😍"]
                                  .slice(0, item.maxRating || 5)
                                  .map((face, i) => (
                                    <span key={i} title={`${i + 1}`}>
                                      {face}
                                    </span>
                                  ))}
                              </div>
                            ) : (
                              <div className="flex gap-1 text-lg text-[#FBBF24]">
                                {Array.from({
                                  length: item.maxRating || 5,
                                }).map((_, i) => (
                                  <span key={i}>★</span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }
                      if (item.type === "project") {
                        const projectOpts = resolvePreviewProjectOptions(
                          item,
                          option.project || []
                        );
                        const multi = item.selectionMode === "multiple";
                        return (
                          <div key={item.id}>
                            <label className="mb-1.5 block text-sm font-semibold capitalize text-[#374151]">
                              {item.name || "Project Interest"}
                              {item.required ? (
                                <span style={{ color: theme }}> *</span>
                              ) : null}
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {projectOpts.length ? (
                                projectOpts.map((p: any) => (
                                  <span
                                    key={p.id}
                                    className="rounded-lg border px-2.5 py-1.5 text-xs font-medium"
                                    style={{
                                      borderColor: `${theme}55`,
                                      color: theme,
                                    }}
                                  >
                                    {multi ? "☐" : "○"} {p.label}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-[#9CA3AF]">
                                  {item.projectScope === "selected"
                                    ? "Select projects in field settings"
                                    : "Load organization to list projects"}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={item.id}>
                          <label className="mb-1.5 block text-sm font-semibold capitalize text-[#374151]">
                            {item.name || item.type}
                            {item.required ? (
                              <span style={{ color: theme }}> *</span>
                            ) : null}
                          </label>
                          {item.type === "textarea" ||
                          item.type === "address" ? (
                            <textarea
                              disabled
                              rows={3}
                              placeholder={item.placeholder || ""}
                              className="w-full resize-none rounded-xl border bg-white px-3 py-2.5 text-sm outline-none"
                              style={{ borderColor: `${theme}55` }}
                            />
                          ) : (
                            <input
                              disabled
                              type={
                                item.type === "time"
                                  ? "time"
                                  : item.type === "date" ||
                                      item.type === "daterange"
                                    ? "date"
                                    : item.type === "number"
                                      ? "number"
                                      : "text"
                              }
                              placeholder={item.placeholder || item.type}
                              className="w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none"
                              style={{ borderColor: `${theme}55` }}
                            />
                          )}
                        </div>
                      );
                    };

                    let stepIndex = 0;
                    return sections.map((sec, si) => {
                      const isStep = sec.section?.sectionType === "step";
                      if (isStep) stepIndex += 1;

                      // Fields before the first section — no header
                      if (!sec.section) {
                        return (
                          <div key={`preamble-${si}`} className="space-y-3">
                            {sec.fields.map((item: any) => renderField(item))}
                          </div>
                        );
                      }

                      const restricted =
                        sec.section?.sectionAccess === "restricted" ||
                        sec.section?.access === "restricted";

                      return (
                        <div
                          key={sec.section.id || si}
                          className="space-y-3 rounded-xl border p-3.5"
                          style={{
                            borderColor: `${theme}40`,
                            backgroundColor: `${theme}08`,
                          }}
                        >
                          {isStep ? (
                            <div
                              className="flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-white"
                              style={{ background: theme }}
                            >
                              <span className="text-sm font-bold">
                                {sec.label}
                                <span className="ml-2 text-xs font-medium opacity-80">
                                  Step {stepIndex}/{stepTotal || 1}
                                </span>
                              </span>
                              {restricted ? (
                                <span className="rounded-full bg-[#C4B5FD] px-2 py-0.5 text-[11px] font-bold text-[#4C1D95]">
                                  Restricted
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2 border-b pb-2"
                              style={{ borderColor: `${theme}33` }}
                            >
                              <h3
                                className="text-sm font-bold"
                                style={{ color: theme }}
                              >
                                {sec.label}
                              </h3>
                              {restricted ? (
                                <span className="rounded-full bg-[#C4B5FD] px-2 py-0.5 text-[11px] font-bold text-[#4C1D95]">
                                  Restricted
                                </span>
                              ) : (
                                <span className="text-[11px] font-medium text-[#9CA3AF]">
                                  Section
                                </span>
                              )}
                            </div>
                          )}

                          {sec.fields.length === 0 ? (
                            <p className="text-xs text-[#9CA3AF]">
                              No fields in this section yet.
                            </p>
                          ) : (
                            sec.fields.map((item: any) => renderField(item))
                          )}
                        </div>
                      );
                    });
                  })()
                )}

                {!items.some((i: any) => i.type === "button") ? (
                <button
                  type="button"
                  className="mt-2 w-full rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm"
                  style={{ backgroundColor: formSettings.theme || "#841C39" }}
                >
                  Submit
                </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      </DndProvider>
      ) : null}
    </div>
  );
};

export default PersonalDetails;
